import { useSyncExternalStore } from 'react';
import type {
  ChatLine, ClientMsg, FormatMeta, Challenge, PublicUser, Role, RoomId, RoomSummary, ServerMsg,
} from '@pgp/shared';

const TOKEN_KEY = 'pgp.token';

export interface RoomView {
  summary: RoomSummary;
  role: Role;
  chat: ChatLine[];
  snapshot: unknown;
}

export interface AppState {
  connection: 'offline' | 'connecting' | 'online';
  user: PublicUser | null;
  formats: FormatMeta[];
  users: PublicUser[];
  challenges: Challenge[];
  rooms: RoomSummary[];
  /** Rooms this client is in, by id. */
  joined: Record<RoomId, RoomView>;
  /** Which room the UI is currently showing, if any. */
  activeRoom: RoomId | null;
  error: string | null;
}

const initial: AppState = {
  connection: 'offline',
  user: null,
  formats: [],
  users: [],
  challenges: [],
  rooms: [],
  joined: {},
  activeRoom: null,
  error: null,
};

type GameHandler = (payload: unknown) => void;

class Net {
  private ws: WebSocket | null = null;
  private listeners = new Set<() => void>();
  private gameHandlers = new Map<RoomId, Set<GameHandler>>();
  /** Game payloads received before a view subscribed to that room. */
  private pendingGame = new Map<RoomId, unknown[]>();
  private reconnectDelay = 500;
  private reconnectTimer: number | undefined;
  state: AppState = initial;

  token: string | null = localStorage.getItem(TOKEN_KEY);

  /* --- store plumbing --- */

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): AppState => this.state;

  private set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }

  /* --- game payload subscriptions --- */

  onGame(roomId: RoomId, fn: GameHandler): () => void {
    let set = this.gameHandlers.get(roomId);
    if (!set) {
      set = new Set();
      this.gameHandlers.set(roomId, set);
    }
    set.add(fn);

    // Anything that arrived before this handler existed. The battle screen is
    // code-split, so between `room.joined` and the view mounting there is a
    // real window -- long enough to lose the opening turn of a battle.
    const queued = this.pendingGame.get(roomId);
    if (queued) {
      this.pendingGame.delete(roomId);
      for (const payload of queued) fn(payload);
    }

    return () => {
      set!.delete(fn);
      if (set!.size === 0) this.gameHandlers.delete(roomId);
    };
  }

  /* --- auth --- */

  async login(username: string, password: string): Promise<void> {
    await this.credentials('/api/login', { username, password });
  }

  async register(
    username: string, password: string, name: string, invite: string
  ): Promise<void> {
    await this.credentials('/api/register', { username, password, name, invite });
  }

  private async credentials(route: string, body: unknown): Promise<void> {
    const res = await fetch(route, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error ?? 'Something went wrong');
    this.token = json.token as string;
    localStorage.setItem(TOKEN_KEY, this.token);
    this.set({ user: json.user as PublicUser, error: null });
    this.connect();
  }

  logout(): void {
    const token = this.token;
    this.token = null;
    localStorage.removeItem(TOKEN_KEY);
    this.ws?.close();
    this.ws = null;
    this.state = initial;
    for (const fn of this.listeners) fn();
    if (token) {
      void fetch('/api/logout', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      });
    }
  }

  /* --- socket --- */

  connect(): void {
    if (!this.token) return;
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    this.set({ connection: 'connecting' });

    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectDelay = 500;
      this.send({ t: 'auth', token: this.token! });
    };

    ws.onmessage = (ev) => this.receive(JSON.parse(ev.data as string) as ServerMsg);

    ws.onclose = () => {
      this.ws = null;
      this.set({ connection: 'offline' });
      if (!this.token) return;
      // Reconnect with backoff. The server holds a player's seat for a while,
      // so a flaky phone connection does not lose the battle.
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = window.setTimeout(() => this.connect(), this.reconnectDelay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 10_000);
    };

    ws.onerror = () => ws.close();
  }

  send(msg: ClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private receive(msg: ServerMsg): void {
    switch (msg.t) {
      case 'auth.ok':
        this.set({ connection: 'online', user: msg.user, formats: msg.formats, error: null });
        break;

      case 'auth.fail':
        localStorage.removeItem(TOKEN_KEY);
        this.token = null;
        this.set({ connection: 'offline', user: null, error: msg.reason });
        break;

      case 'lobby.state':
        this.set({ users: msg.users, challenges: msg.challenges, rooms: msg.rooms });
        break;

      case 'room.joined': {
        // The snapshot is a complete replay for this audience, so any payload
        // buffered from before it would double-apply.
        this.pendingGame.delete(msg.room.id);
        const view: RoomView = {
          summary: msg.room, role: msg.role, chat: msg.chat, snapshot: msg.snapshot,
        };
        this.set({
          joined: { ...this.state.joined, [msg.room.id]: view },
          activeRoom: msg.room.id,
        });
        break;
      }

      case 'room.update': {
        const existing = this.state.joined[msg.room.id];
        if (!existing) break;
        this.set({
          joined: { ...this.state.joined, [msg.room.id]: { ...existing, summary: msg.room } },
        });
        break;
      }

      case 'room.chat': {
        const existing = this.state.joined[msg.roomId];
        if (!existing) break;
        this.set({
          joined: {
            ...this.state.joined,
            [msg.roomId]: { ...existing, chat: [...existing.chat, msg.line] },
          },
        });
        break;
      }

      case 'room.left': {
        this.pendingGame.delete(msg.roomId);
        const { [msg.roomId]: _gone, ...rest } = this.state.joined;
        this.set({
          joined: rest,
          activeRoom: this.state.activeRoom === msg.roomId ? null : this.state.activeRoom,
        });
        break;
      }

      case 'game': {
        const handlers = this.gameHandlers.get(msg.roomId);
        if (handlers?.size) {
          for (const fn of handlers) fn(msg.payload);
        } else {
          const queue = this.pendingGame.get(msg.roomId) ?? [];
          queue.push(msg.payload);
          this.pendingGame.set(msg.roomId, queue);
        }
        break;
      }

      case 'error':
        this.set({ error: msg.message });
        window.setTimeout(() => this.set({ error: null }), 5000);
        break;
    }
  }

  /* --- convenience actions --- */

  openRoom(roomId: RoomId | null): void {
    this.set({ activeRoom: roomId });
  }

  clearError(): void {
    this.set({ error: null });
  }
}

export const net = new Net();

export function useApp(): AppState {
  return useSyncExternalStore(net.subscribe, net.getSnapshot);
}
